import { createMultiStyleConfigHelpers } from "@chakra-ui/react";
import { inputAnatomy, selectAnatomy } from "@chakra-ui/anatomy";

/**
 * Input / Select / Textarea share the `filled-premium` field recipe (the DEFAULT variant):
 * sand.50 fill, 1px sand border, 10px radius, 2.75rem tall, weight 600; hover → white; focus → white + navy border + green ring;
 * `isReadOnly` → sand.100 with a dashed border (no per-field styling needed); `isInvalid` → 1px red ring at rest, 4px red focus ring.
 * Sizes are expressed through Chakra's --input-* variables so InputLeftElement/RightElement stay aligned.
 */
export const premiumField = {
  bg: "sand.50",
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "md",
  color: "text.primary",
  fontWeight: 600,
  _placeholder: { color: "text.muted", fontWeight: 500 },
  _hover: { bg: "surface.card", borderColor: "border.strong" },
  _focusVisible: { bg: "surface.card", borderColor: "navy.900", boxShadow: "outline" },
  // Resting invalid state is a light 1px red ring; focus on an invalid field gets its own (nested, so it wins) danger ring.
  _invalid: {
    borderColor: "red.500",
    boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
    _focusVisible: { bg: "surface.card", borderColor: "red.600", boxShadow: "outlineDanger" },
  },
  _readOnly: {
    bg: "sand.100",
    borderStyle: "dashed",
    color: "text.secondary",
    cursor: "default",
    _focusVisible: { boxShadow: "outline", borderColor: "navy.900" },
  },
  _disabled: { opacity: 0.55, bg: "sand.100" },
};

const sizeVars = {
  sm: {
    "--input-height": "2.25rem",
    "--input-padding": "0.75rem",
    "--input-font-size": "0.8125rem",
    "--input-border-radius": "radii.md",
  },
  md: {
    "--input-height": "2.75rem",
    "--input-padding": "1rem",
    "--input-font-size": "0.9375rem",
    "--input-border-radius": "radii.md",
  },
  lg: {
    "--input-height": "3.25rem",
    "--input-padding": "1.125rem",
    "--input-font-size": "1rem",
    "--input-border-radius": "radii.md",
  },
};

const inputHelpers = createMultiStyleConfigHelpers(inputAnatomy.keys);
export const Input = inputHelpers.defineMultiStyleConfig({
  variants: {
    "filled-premium": {
      field: premiumField,
      addon: { bg: "sand.100", border: "1px solid", borderColor: "border.default", color: "text.muted", fontWeight: 600 },
      element: { color: "sand.500" },
    },
  },
  sizes: {
    sm: { field: sizeVars.sm, group: sizeVars.sm },
    md: { field: sizeVars.md, group: sizeVars.md },
    lg: { field: sizeVars.lg, group: sizeVars.lg },
  },
  defaultProps: { variant: "filled-premium", size: "md" },
});

const selectHelpers = createMultiStyleConfigHelpers(selectAnatomy.keys);
export const Select = selectHelpers.defineMultiStyleConfig({
  variants: { "filled-premium": { field: premiumField, icon: { color: "sand.600" } } },
  sizes: {
    sm: { field: sizeVars.sm },
    md: { field: sizeVars.md },
    lg: { field: sizeVars.lg },
  },
  defaultProps: { variant: "filled-premium", size: "md" },
});

export const Textarea = {
  variants: { "filled-premium": { ...premiumField, py: 3, minH: "8rem", lineHeight: 1.5 } },
  sizes: {
    sm: sizeVars.sm,
    md: sizeVars.md,
    lg: sizeVars.lg,
  },
  defaultProps: { variant: "filled-premium", size: "md" },
};

export const FormLabel = {
  baseStyle: {
    fontFamily: "body",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "text.secondary",
    mb: 1.5,
    _invalid: { color: "red.600" },
    _disabled: { opacity: 0.6 },
  },
};

export const Form = {
  baseStyle: {
    requiredIndicator: { color: "leaf.700", ml: 1 },
    helperText: { color: "text.muted", fontSize: "0.75rem", fontWeight: 500, mt: 1.5 },
  },
};

export const FormError = {
  baseStyle: {
    text: { fontSize: "0.75rem", color: "red.600", fontWeight: 600, mt: 1.5 },
    icon: { color: "red.600" },
  },
};
